import React from 'react';
import cx from 'classnames';
import { IGlyph, Icon } from '@treelab/panda';

import { FieldRenderProps, IOption } from '../types';
import { ARROW_DROPDOWN } from '../base/icons';
import { InputTitle } from '../../components/Input';

/**
 * Dropdown component.
 */
interface IInputDropdown extends FieldRenderProps<HTMLElement> {
  /** Passed down classname. */
  className?: string;
  /** Empty Option Title. Pass formatMessage to have this translated. If not, it's an empty string*/
  emptyOptionTitle?: string;
  /** Function for translation of options */
  formatMessage?: (id: string) => string;
  /** Glyph for svg */
  icon?: IGlyph;
  /** Optional size for icon */
  iconSize?: { height?: number; width?: number };
  /** Optional id indicator */
  id?: string;
  /** True if we want to include the empty first option */
  includeEmptyOption?: boolean;
  /** Is disabled */
  isDisabled?: boolean;
  /** If background has a solid color */
  isReverse?: boolean;
  /** Is small */
  isSmall?: boolean;
  /** Mandatory options list. If formatted message is passed, then it will translate */
  options: IOption[];
  /** Optional placeholder */
  placeholder?: string;
  /** Optional title */
  title?: string;
}

export const InputDropdown: React.FC<IInputDropdown> = ({
  className,
  formatMessage,
  emptyOptionTitle,
  id,
  input: { name, onBlur, onChange, onFocus, value },
  icon,
  iconSize,
  includeEmptyOption,
  isSmall = false,
  isDisabled = false,
  isReverse = false,
  options = [],
  meta: { active, error, submitError, submitting, touched, invalid, dirtySinceLastSubmit },
  placeholder,
  title,
}) => {
  const errorMsg = error || submitError;
  const isValidationError = invalid && touched && !!error;
  const isSubmitError = !!invalid && !dirtySinceLastSubmit && !!submitError;
  const isError = (isValidationError || isSubmitError) && !submitting;
  const combinedContainerClass = cx({
    'panda-input-container': true,
    [className || '']: !!className,
  });
  // Most styles are copied from inputText/index.css
  const combinedInputClass = cx({
    'panda-input-dropdown': true,
    'panda-input--small': isSmall,
    'panda-input--large': !isSmall,
    'panda-typography--P2': isSmall,
    'panda-typography--P1': !isSmall,
    'panda-input-text--focused': !!active,
    'panda-input-text--error': isError,
    'panda-input-text--disabled': isDisabled,
  });
  const combinedIconClass = cx({
    'panda-input-icon': true,
    'panda-input-icon--with-title': !!title,
    'panda-input-icon--without-title': !title,
    'panda-input-icon--large': !isSmall,
    'panda-input-icon--small': isSmall,
  });
  const combinedErrorClass =
    !!isError &&
    cx({
      'panda-input-error': true,
      'panda-input-error--small': isSmall,
    });
  const emptyOption = {
    name: !!emptyOptionTitle
      ? formatMessage
        ? formatMessage(emptyOptionTitle)
        : emptyOptionTitle
      : '',
    value: '',
  };

  return (
    <div className={combinedContainerClass}>
      {!!title && <InputTitle isReverse={isReverse} isSmall={isSmall} title={title} />}
      <Icon
        className={combinedIconClass as string}
        icon={icon || ARROW_DROPDOWN}
        size={iconSize || { height: isSmall ? 6 : 8 }}
      />
      <select
        className={combinedInputClass}
        disabled={isDisabled || submitting}
        id={id}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        value={value}
      >
        {(includeEmptyOption ? [emptyOption].concat(options) : options).map(
          (option: IOption, index: number) => (
            <option key={index} value={option.value}>
              {formatMessage ? formatMessage(option.name) : option.name}
            </option>
          ),
        )}
      </select>
      {!!isError && <p className={combinedErrorClass || ''}>{errorMsg}</p>}
    </div>
  );
};
